import { RegionsCode } from '../../constants/region';
import { SpaceMembershipRole } from '../../constants/space';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { I18nLocale, RewardType, UserId, Uuid } from '../Base';
import { RepositoryElement } from '../Repository';

export enum SpaceMembershipState {
  InGroup = 'inGroup',
  NotInGroup = 'notInGroup',
  WaitingForApproval = 'waitingForApproval',
  Invited = 'invited',
  BlockedByGroup = 'blockedByGroup',
  BlockingGroup = 'blockingGroup',
}

interface LearningSchedule<T = number> {
  completedAt: T;
  days: number[];
}

export interface RevisionSetting<T = number> {
  partitionId: string | null;
  completedAt: T;
}

type StatisticsKey = `${
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'final'
  | 'seasonFinal'
  | 'yearFinal'
  | 'grandFinal'}${
  | 'First'
  | 'Second'
  | 'Third'
  | 'Gold'
  | 'Silver'
  | 'Bronze'
  | 'Others'
  | 'Nil'}Count`;

interface Tickets {
  weekly?: number;
  monthly?: number;
  quarterly?: number;
  yearly?: number;
  final?: number;
}

interface Puzzle {
  collectedPieceCount: number;
  claimedRewardCount: number;
}

export interface SpaceBalance {
  [RewardType.ClassroomCredit]?: number;
}

export enum LastAccessItemType {
  Slideshow = 'slideshow',
  Game = 'game',
}

export interface LastAccess {
  gameSetId?: string;
  gameCollectionId?: string;
  itemType?: LastAccessItemType;
  slideshowId?: string;
  gameId?: string;
  // gameType?: GameType;
}

export interface SpaceMembershipJson<T = number> {
  readonly classroomId: Uuid; //identifier
  readonly userId: UserId; // identifier
  createdAt: T;
  lastModifiedAt: T;
  lastSeenAt?: T;
  lastModifiedRegionAt?: T;
  firstJoinedAt?: T;
  lastJoinedAt?: T;
  lastAccessedAt?: T;
  streamInSyncAt?: T;
  state: SpaceMembershipState;
  role: SpaceMembershipRole;
  tier: null | { tierId: Uuid; expiredAt?: Date };
  preferredWritingSystem: I18nLocale;
  preferredSpeakingAccent: I18nLocale;
  learningLocales: I18nLocale[] | null;
  learningSchedule: LearningSchedule<T> | null;
  revisionSettings: RevisionSetting<T>[];
  participationRegion: RegionsCode;
  isFavourite: boolean;
  statistics: Partial<Record<StatisticsKey, number>>;
  tickets: Tickets;
  puzzle: Puzzle;
  balance: SpaceBalance;
  lastAccess?: LastAccess | null; // null for sync params
  teamId?: Uuid;
}

export class SpaceMembership
  extends RepositoryElement
  implements SpaceMembershipJson<Date>
{
  readonly classroomId!: Uuid; //identifier
  readonly userId!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  lastSeenAt?: Date;
  lastModifiedRegionAt?: Date;
  firstJoinedAt?: Date;
  lastJoinedAt?: Date;
  lastAccessedAt?: Date;
  streamInSyncAt?: Date;
  state!: SpaceMembershipState;
  role!: SpaceMembershipRole;
  tier!: null | { tierId: Uuid; expiredAt?: Date };
  preferredWritingSystem!: I18nLocale;
  preferredSpeakingAccent!: I18nLocale;
  learningLocales!: I18nLocale[] | null;
  learningSchedule!: LearningSchedule<Date> | null;
  revisionSettings!: RevisionSetting<Date>[];
  participationRegion!: RegionsCode;
  isFavourite!: boolean;
  statistics!: Partial<Record<StatisticsKey, number>>;
  tickets!: Tickets;
  puzzle!: Puzzle;
  balance!: SpaceBalance;
  lastAccess?: LastAccess | null; // null for sync params
  teamId?: string;

  constructor(spaceMembership: SpaceMembershipJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(spaceMembership));
  }

  id(): string {
    return `${this.classroomId}_${this.userId}`;
  }

  toJSON(): SpaceMembershipJson {
    return deepTransformDate(this) as unknown as SpaceMembershipJson;
  }

  hasLocale(locale: I18nLocale): boolean {
    return (
      this.learningLocales === null || this.learningLocales.includes(locale)
    );
  }

  get isInGroup(): boolean {
    return this.state === SpaceMembershipState.InGroup;
  }
  get isNotInGroup() {
    return this.state === SpaceMembershipState.NotInGroup;
  }
  get isWaitingForApproval() {
    return this.state === SpaceMembershipState.WaitingForApproval;
  }
  get isInvited() {
    return this.state === SpaceMembershipState.Invited;
  }
  get isBlockedByGroup() {
    return this.state === SpaceMembershipState.BlockedByGroup;
  }
  get isBlockingGroup() {
    return this.state === SpaceMembershipState.BlockingGroup;
  }
  get isRoot() {
    return this.isInGroup && this.role === SpaceMembershipRole.Root;
  }
  get isAdmin() {
    return this.isInGroup && this.role === SpaceMembershipRole.Admin;
  }
  get isModerator() {
    return this.isInGroup && this.role === SpaceMembershipRole.Moderator;
  }
  get isMember() {
    return this.isInGroup && this.role === SpaceMembershipRole.Member;
  }
  get isFollower() {
    return this.isInGroup && this.role === SpaceMembershipRole.Follower;
  }
  get isAtLeastAdmin() {
    return (
      this.isInGroup &&
      [SpaceMembershipRole.Root, SpaceMembershipRole.Admin].includes(this.role)
    );
  }
  get isAtLeastModerator() {
    return (
      this.isInGroup &&
      [
        SpaceMembershipRole.Root,
        SpaceMembershipRole.Admin,
        SpaceMembershipRole.Moderator,
      ].includes(this.role)
    );
  }
  get isAtLeastMember() {
    return (
      this.isInGroup &&
      [
        SpaceMembershipRole.Root,
        SpaceMembershipRole.Admin,
        SpaceMembershipRole.Moderator,
        SpaceMembershipRole.Member,
      ].includes(this.role)
    );
  }
  get isAtMostAdmin() {
    return (
      this.isInGroup &&
      [
        SpaceMembershipRole.Admin,
        SpaceMembershipRole.Moderator,
        SpaceMembershipRole.Member,
        SpaceMembershipRole.Follower,
      ].includes(this.role)
    );
  }
  get isAtMostModerator() {
    return (
      this.isInGroup &&
      [
        SpaceMembershipRole.Moderator,
        SpaceMembershipRole.Member,
        SpaceMembershipRole.Follower,
      ].includes(this.role)
    );
  }
  get isAtMostMember() {
    return (
      this.isInGroup &&
      [SpaceMembershipRole.Member, SpaceMembershipRole.Follower].includes(
        this.role,
      )
    );
  }

  // get subtype(): SpaceMembershipListSubtype {
  //   return this.state === SpaceMembershipState.InGroup ? this.role : this.state;
  // }

  // get uiStatus(): MembershipStatus {
  //   if (this.isInGroup) {
  //     return MembershipStatus.Joined;
  //   } else if (this.isWaitingForApproval) {
  //     return MembershipStatus.Pending;
  //   } else {
  //     return MembershipStatus.NotJoined;
  //   }
  // }

  // incrementBalance(increase: Partial<SpaceBalance>): void {
  //   sumNumberRecords(this.balance, increase);
  // }

  // incrementPuzzle(increase: Partial<Puzzle>): void {
  //   sumNumberRecords(this.puzzle, increase);
  // }

  checkPermission(minPermissionRole: SpaceMembershipRole): boolean {
    const SpaceMembershipRoleLevel: Record<SpaceMembershipRole, number> = {
      [SpaceMembershipRole.Root]: 4,
      [SpaceMembershipRole.Admin]: 3,
      [SpaceMembershipRole.Moderator]: 2,
      [SpaceMembershipRole.Member]: 1,
      [SpaceMembershipRole.Follower]: 0,
    };
    const thisRoleLevel = SpaceMembershipRoleLevel[this.role];
    const minRoleLevel = SpaceMembershipRoleLevel[minPermissionRole];
    return thisRoleLevel >= minRoleLevel;
  }
}
